import cardValidator from 'card-validator';

import { LoginCreditCard, Unprotect } from 'persistable-types';

export default function normalizeCreditCard(
  creditCard: Partial<Unprotect<LoginCreditCard, string>>,
): Unprotect<LoginCreditCard, string> | undefined {
  const normalizedNumber =
    creditCard.number ?
      normalizeCreditCardNumber(creditCard.number)
    : { number: '', displayMaskedNumber: '', displayType: '' };

  const normalizedCreditCard = {
    pin: creditCard.pin ?? '',
    securityCode: creditCard.securityCode ?? '',
    ...normalizedNumber,
    cardHolder: creditCard.cardHolder?.trim() ?? '',
    expiration: creditCard.expiration ? normalizeCreditCardExpiration(creditCard.expiration) : '',
  };

  // all values are empty string, return nothing
  if (Object.values(normalizedCreditCard).every((v) => v === '')) {
    return undefined;
  }

  return normalizedCreditCard;
}

export function normalizeCreditCardNumber(inputNumber: string) {
  const cardNumberValidation = cardValidator.number(inputNumber);

  if (!cardNumberValidation.card || !cardNumberValidation.isValid) {
    const number = inputNumber.trim();
    // we can't really go by components for invalid numbers, so we just mask every digit with an
    // asterisk to be safe
    const displayMaskedNumber = number.replace(/\d/g, '*');
    return { number, displayMaskedNumber, displayType: cardNumberValidation.card?.niceType ?? '' };
  }
  const { card } = cardNumberValidation;
  const cardNumberDigits = inputNumber.replace(/\D/g, '');

  const offsets = [0, ...card.gaps, cardNumberDigits.length];
  const components = [];

  for (let i = 0; offsets[i] < cardNumberDigits.length; i++) {
    const start = offsets[i];
    const end = Math.min(offsets[i + 1], cardNumberDigits.length);
    components.push(cardNumberDigits.substring(start, end));
  }

  const number = components.join(' ');

  // mask digits in every component except the last with asterisks
  const displayMaskedNumber = components
    .map((c, i) => (i === components.length - 1 ? c : c.replace(/\d/g, '*')))
    .join(' ');

  return { number, displayMaskedNumber, displayType: cardNumberValidation.card.niceType };
}

export function normalizeCreditCardExpiration(expiration: string) {
  const expirationValidation = cardValidator.expirationDate(expiration, Infinity);

  if (!expirationValidation.isValid || !expirationValidation.month || !expirationValidation.year) {
    // cardValidator doesn't consider this a valid expiration date. It might still be well-formed
    // but in the past. Do a basic pass to get it looking nice if input as mm/yy or mm/yyyy
    const match = /^\s*(?<month>\d?\d)\s*\/\s*(?<year>(\d\d\d|\d)?\d)\s*$/.exec(expiration);
    if (match) {
      return `${match.groups?.month.padStart(2, '0')} / ${match.groups?.year
        .substring(match.groups.year.length - 2)
        .padStart(2, '0')}`;
    }
    return expiration.trim();
  }
  // output year can be 2 or 4 digits, always normalize to 2 for consistency
  const twoDigitYear = expirationValidation.year.substring(expirationValidation.year.length - 2);
  return `${expirationValidation.month} / ${twoDigitYear}`;
}
